/**
 * FeedPostCard — Figma 38:904 LIST card projection.
 *
 * ACT-INDEEP-PRODUCTION-FEED-FIGMA-REPAIR01 (Phase D)
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY (feed list + comments link).
 *
 * The card is the AUTHORITY for two Anti-Paper-RED invariants:
 *
 *   R1 (wire boundary): the prop surface intentionally does NOT
 *       include `authorAge`. The /api/v1/feed/ PostResponse has no
 *       age field. The card cannot mis-render an age it never
 *       receives.
 *
 *   R3 (anonymity): the anonymous projection is driven by the
 *       `isAnonymous` boolean flag, NEVER by string substitution.
 *       A hostile `authorName` (e.g. "SHOULD_NOT_LEAK") MUST be
 *       replaced by "Аноним" in the rendered DOM when `isAnonymous`
 *       is true.
 *
 * Accessibility:
 * - Renders a React Router <Link> so middle-click / cmd-click open
 *   in a new tab.
 * - Keyboard reachable: native anchor semantics with Tab + Enter.
 * - Touch target ≥ 44×44 via min-height / min-width (FeedPostCard.css).
 * - Accessible name on the comments control: aria-label.
 * - Like control is a NON-interactive <span> in Phase D (LIKE
 *   mutation is out of scope; LIKED_FILL_STATE is
 *   DEFERRED_NO_FIGMA_AUTHORITY).
 */
import { Link } from 'react-router-dom';
import { buildFeedCommentsPath } from '@/routes';
import { formatRelativeTime } from '@/features/comments/formatRelativeTime';

export interface FeedPostCardProps {
  /** Canonical post ID — used to build the comments route via the
   *  safe-path builder. */
  postId: string;
  /** Author display name as it appears on the wire. For anonymous
   *  posts the consumer MUST also pass `isAnonymous: true` so the
   *  card projects "Аноним" instead of this value. R3 binding. */
  authorName: string;
  /** Post body. */
  body: string;
  /** Visible comment count. */
  commentCount: number;

  /** Optional avatar initials. When present, renders a circular
   *  avatar with the initials as text content (Figma 38:904). */
  authorInitials?: string;
  /** Optional ISO-8601 created_at. When present, the card renders
   *  a relative timestamp under the author. */
  createdAt?: string;
  /** Reference time for the relative timestamp projection. Required
   *  when `createdAt` is provided. Pure / deterministic — caller
   *  supplies the reference so tests can pin the assertion. */
  referenceTime?: Date;
  /** Optional like count. When present, renders a display-only
   *  <span> with the count text. NOT a button. */
  likeCount?: number;
  /** Anonymity flag. When true, the author cell renders "Аноним"
   *  regardless of `authorName` (R3). Defaults to false when
   *  omitted. */
  isAnonymous?: boolean;
}

/**
 * Anonymous author display name — Figma 38:904.
 *
 * Pinned as a constant so a future rename cannot silently diverge
 * from Figma. The Playwright spec (F-6) and the JSDOM authority
 * suite (FeedPostCard.test.tsx) both read this exact string.
 */
export const ANONYMOUS_DISPLAY_NAME = 'Аноним';

/**
 * Figma 38:904 feed post list card.
 *
 * Composition (in DOM order):
 *   - Avatar (if authorInitials provided)
 *   - Author block (always):
 *       - authorName verbatim when isAnonymous !== true
 *       - "Аноним" when isAnonymous === true (R3)
 *   - Relative timestamp (if createdAt + referenceTime provided)
 *   - Body
 *   - Footer:
 *       - Like count (display-only, if likeCount provided)
 *       - Comments affordance (always)
 */
export function FeedPostCard({
  postId,
  authorName,
  body,
  commentCount,
  authorInitials,
  createdAt,
  referenceTime,
  likeCount,
  isAnonymous = false,
}: FeedPostCardProps): JSX.Element {
  // buildFeedCommentsPath throws RangeError on unsafe IDs. For the
  // production-feed case the caller is trusted (it reads IDs from
  // the backend contract) but we still fail loud if a future
  // caller misuses this card with unsanitized text.
  const commentsHref = buildFeedCommentsPath(postId);

  // R3: anonymity is driven by the boolean flag, never by string
  // matching the authorName.
  const projectedAuthorName = isAnonymous
    ? ANONYMOUS_DISPLAY_NAME
    : authorName;

  const showTimestamp = typeof createdAt === 'string'
    && referenceTime instanceof Date;
  const showLike = typeof likeCount === 'number';
  const showAvatar = typeof authorInitials === 'string'
    && authorInitials.length > 0;

  return (
    <article className="feed-post-card" data-testid={`feed-post-${postId}`}>
      <header className="feed-post-card-header">
        {showAvatar && (
          <span
            className="feed-post-card-avatar"
            data-testid={`feed-post-${postId}-avatar`}
            aria-hidden="true"
          >
            {authorInitials}
          </span>
        )}
        <span
          className="feed-post-card-author"
          data-testid={`feed-post-${postId}-author`}
        >
          {projectedAuthorName}
        </span>
        {showTimestamp && (
          <span
            className="feed-post-card-time"
            data-testid={`feed-post-${postId}-time`}
          >
            {/* CORRECTION08 (P1-02): reuses the canonical
                formatRelativeTime from
                @/features/comments/formatRelativeTime. The
                FeedPostCard is intentionally NOT a second authority
                on Russian relative-time copy — the canonical
                formatter is the single source of truth (see WAL
                §Phase A — existing reusable machinery). */}
            {formatRelativeTime(createdAt as string, referenceTime as Date)}
          </span>
        )}
      </header>
      <p className="feed-post-card-body">{body}</p>
      <footer className="feed-post-card-footer">
        {showLike && (
          <span
            className="feed-post-card-likes"
            data-testid={`feed-post-${postId}-likes`}
          >
            <svg
              className="feed-post-card-likes-icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span className="feed-post-card-likes-count">{likeCount}</span>
          </span>
        )}
        <Link
          to={commentsHref}
          className="feed-post-card-comments"
          aria-label={`Комментарии (${commentCount})`}
          data-testid={`feed-post-${postId}-comments`}
        >
          <svg
            className="feed-post-card-comments-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          <span className="feed-post-card-comments-label">Комментарии</span>
          <span className="feed-post-card-comments-count">{commentCount}</span>
        </Link>
      </footer>
    </article>
  );
}